import { flushSync } from "react-dom";
import { forOwn } from "../../utils/lodash";
import { Voxco, PublicApi } from "../../public";
import {
    FileUploadEvent,
    SetVariableMentionsEvent,
    VoxcoEvent,
    SetAppointmentEvent,
    SetLoopRowEvent,
    NavigationBranchToEvent,
    NavigationDisplayMessageEvent,
    NavigationQuitEvent,
    ConnectionStartEvent,
    SetPageMessagesEvent,
    SetPhoneEvent,
    SetEmailEvent,
    VoxcoEventTypes,
    SetTimeZoneEvent,
} from "../../public/events";
import { MiddlewareType, StoreType, DispatchType, ActionTypes } from "../actions/types";
import {
    SET_VARIABLE_MENTIONS,
    NAVIGATION_NEXT_PAGE,
    NAVIGATION_PREVIOUS_PAGE,
    NAVIGATION_QUIT,
    RESPONDENT_SET_APPOINTMENT,
    RESPONDENT_SET_EMAIL,
    RESPONDENT_SET_PHONE,
    RESPONDENT_SET_TIMEZONE,
    SET_LOOP_ROW,
    connectionStart,
    connectionDisconnected,
    setVariableMentions,
    navigationBranchTo,
    navigationDisplayMessage,
    navigationNextPage,
    navigationPreviousPage,
    navigationQuit,
    respondentSetPhone,
    respondentSetEmail,
    respondentSetTimeZone,
    setPageMessagesEnabled,
    respondentSetAppointment,
    setLoopRow,
} from "../actions";

/**
 * Voxco event -> Redux dispatcher
 * @param store Redux store
 * @param next Dispatch actions onto the next middleware.
 * Prevents actions being intercepted by voxcoEventDispatcher and creating a loop.
 */
const voxcoEventListener = (store: StoreType, next: DispatchType) => ({
    connect: (ev: ConnectionStartEvent) => next(connectionStart(ev.url)),
    disconnect: () => next(connectionDisconnected()),
    // Use flushSync so DOM is immediately updated with new mentions
    setVariableMentions: (ev: SetVariableMentionsEvent) =>
        flushSync(() => {
            next(setVariableMentions(ev.variableName, ev.builder, ev.matrixIndex));
        }),
    branchTo: (ev: NavigationBranchToEvent) => next(navigationBranchTo(ev)),
    displayMessage: (ev: NavigationDisplayMessageEvent) =>
        next(navigationDisplayMessage(ev.message)),
    nextPage: () => next(navigationNextPage()),
    previousPage: () => next(navigationPreviousPage()),
    quit: (ev: NavigationQuitEvent) => next(navigationQuit(ev.dispositionCode, ev.options)),
    setPhone: (ev: SetPhoneEvent) => next(respondentSetPhone(ev.phone)),
    setEmail: (ev: SetEmailEvent) => next(respondentSetEmail(ev.email)),
    setTimeZone: (ev: SetTimeZoneEvent) => next(respondentSetTimeZone(ev.timeZone)),
    setPageMessages: (ev: SetPageMessagesEvent) => next(setPageMessagesEnabled(ev.enabled)),
    setAppointment: (ev: SetAppointmentEvent) => next(respondentSetAppointment(ev.appointment)),
    setLoopRow: (ev: SetLoopRowEvent) => next(setLoopRow(ev.blockName, ev.row)),
    fileUploadSelected(ev: FileUploadEvent) {
        if (ev.file) {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", "/Upload", true);
            xhr.setRequestHeader("Content-type", "file");
            const data = new FormData();
            data.append("file", ev.file);
            xhr.send(data);
        }
    },
});

/**
 * Redux event -> Voxco event dispatcher
 * @param action Action received from redux.
 * @param voxco Public api to dispatch events.
 */
const voxcoEventDispatcher = (
    action: ActionTypes,
    next: DispatchType,
    voxco: PublicApi,
): ActionTypes => {
    switch (action.type) {
        case SET_VARIABLE_MENTIONS:
            voxco.trigger("setVariableMentions", action);
            return undefined;
        case NAVIGATION_NEXT_PAGE:
            voxco.trigger("nextPage", {});
            return undefined;
        case NAVIGATION_PREVIOUS_PAGE:
            voxco.trigger("previousPage", {});
            return undefined;
        case NAVIGATION_QUIT:
            voxco.trigger("quit", action);
            return undefined;
        case RESPONDENT_SET_APPOINTMENT:
            voxco.trigger("setAppointment", action);
            return undefined;
        case RESPONDENT_SET_EMAIL:
            voxco.trigger("setEmail", action);
            return undefined;
        case RESPONDENT_SET_PHONE:
            voxco.trigger("setPhone", action);
            return undefined;
        case RESPONDENT_SET_TIMEZONE:
            voxco.trigger("setTimeZone", action);
            return undefined;
        case SET_LOOP_ROW:
            voxco.trigger("setLoopRow", action);
            return undefined;
        default:
            return next(action);
    }
};

/**
 * Hooks into redux events to expose them to the custom javascript.
 * @param store
 */
export const publicConnector: MiddlewareType = (store) => (next) => {
    Voxco.store = store;
    Voxco.dispatch = next;
    // Listen to Voxco events and dispatch them to the store.
    forOwn(voxcoEventListener(store, next), (value, key: keyof VoxcoEventTypes) =>
        Voxco.on(key, (ev: VoxcoEvent) => {
            // Prevent default behavior if requested.
            if (ev.defaultPrevented) return ev;
            // Call the dispatcher.
            (value as any)(ev);
            return ev;
        }),
    );
    // Listen to redux events and trigger Voxco event listeners.
    return (action) => voxcoEventDispatcher(action, next, Voxco) as any;
};
